import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Settings, Square } from 'lucide-react';
import { CgProfile } from 'react-icons/cg';
import { useRootSelector } from '@/hooks/selector.hook';
import { getSidebarGroups } from '@/utils/sidebar';
import { resolveMenuIcon } from '@/components/Sidebar/api-icon-map';

export default function Breadcrumb() {
  const breadcrumbItems = useRootSelector((state) => state.breadcrumb.items);
  const { dataSidebar } = useRootSelector((state) => state.sidebar);
  const location = useLocation();

  if (breadcrumbItems.length === 0) {
    return null;
  }

  const firstItem = breadcrumbItems[0];
  const isDetailPage = breadcrumbItems.length > 1;

  const normalizeRouteForMatch = (route?: string | null) => {
    if (!route) return '';
    const safeRoute = route.startsWith('/') ? route : `/${route}`;
    return safeRoute === '/' ? safeRoute : safeRoute.replace(/\/+$/, '');
  };

  const internalRouteIconMap = new Map([
    ['/admin/profile', CgProfile],
    ['/admin/settings', Settings],
  ]);

  const findModuleIcon = () => {
    const normalizedPath = normalizeRouteForMatch(location.pathname);
    const routeHint = normalizeRouteForMatch(firstItem.link);
    const internalIcon =
      internalRouteIconMap.get(routeHint) || internalRouteIconMap.get(normalizedPath);

    if (internalIcon) {
      return internalIcon;
    }

    // Prefer the explicit breadcrumb link if provided, otherwise match by current path prefix.
    const apiGroups = getSidebarGroups(dataSidebar);
    const candidates = apiGroups.flatMap((g) =>
      g.items.map((it) => ({
        label: it.label,
        route: it.route || '',
        icon: resolveMenuIcon({ icon: typeof it.icon === 'string' ? it.icon : null, route: it.route }),
      })),
    );

    const matched =
      candidates.find((item) => normalizeRouteForMatch(item.route) === routeHint) ||
      candidates
        .filter((item) => {
          const route = normalizeRouteForMatch(item.route);
          return route && (normalizedPath === route || normalizedPath.startsWith(`${route}/`));
        })
        .sort(
          (a, b) => normalizeRouteForMatch(b.route).length - normalizeRouteForMatch(a.route).length,
        )[0];

    return matched?.icon ?? Square;
  };

  const ModuleIcon = findModuleIcon();

  return (
    <div className="hidden lg:block">
      <nav className="flex items-center gap-[0.6rem] lg:hidden" aria-label="Breadcrumb mobile">
        {isDetailPage && firstItem.link ? (
          <Link
            to={firstItem.link}
            className="flex items-center gap-[0.6rem] text-[#2563EB] transition-opacity duration-200 hover:opacity-80"
          >
            <span className="text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] text-[#2563EB]">
              {firstItem.title}
            </span>
          </Link>
        ) : (
          <div className="flex items-center gap-[0.6rem]">
            <span className="text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] text-[#2563EB]">
              {firstItem.title}
            </span>
          </div>
        )}
      </nav>

      <nav
        className="hidden items-center gap-[0.8rem] text-[1.4rem] lg:flex"
        aria-label="Breadcrumb"
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isSecondToLast = index === breadcrumbItems.length - 2;
          const withModuleIcon = index === 0;

          return (
            <div key={`${item.title}-${index}`} className="flex items-center gap-[0.8rem]">
              {item.link && !isLast ? (
                <Link
                  to={item.link}
                  className="flex items-center gap-[0.6rem] text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] sub-text transition-colors duration-200 hover:text-[#2563EB]"
                >
                  {withModuleIcon && <ModuleIcon className="h-[1.6rem] w-[1.6rem] shrink-0" />}
                  {item.title}
                </Link>
              ) : (
                <span
                  className={`flex items-center gap-[0.6rem] text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] ${
                    isLast ? 'text-main' : 'sub-text'
                  }`}
                >
                  {withModuleIcon && <ModuleIcon className="h-[1.6rem] w-[1.6rem] shrink-0" />}
                  {item.title}
                </span>
              )}
              {!isLast && (
                <ChevronRight
                  className={`h-[1.6rem] w-[1.6rem] ${
                    isSecondToLast ? 'text-main' : 'text-[#7988A4]'
                  }`}
                />
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
